import { MapPin, Navigation, Car, Play } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, CTAButton, Kicker } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { EVENT } from "../lib/site";

export default function RaceMap() {
  return (
    <>
      <Seo title="Race Map & Course" path="/race-map" description="Explore the 5 km course video and interactive map at Casablanca Beach, Grimsby, ON." />
      <PageHeader
        chapter="the course"
        title="Know every turn of the trot."
        sub="A flat, scenic loop along the Lake Ontario shoreline. Preview the course below and get directions to the start line."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-4 pb-12">
        <Reveal>
          <Kicker>interactive route</Kicker>
          <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl">Explore the full 5 km route</h2>
          <div className="mt-6 relative rounded-3xl overflow-hidden shadow-warm-lg border border-black/5 h-[480px] sm:h-[560px] bg-cream-200">
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://onthegomap.com/?route=01a0ca29-b68e-7075-8e57-eeb02bd158f0&context=share"
              title="Grimsby On The Lake Turkey Trot 5 km route"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="route-map"
            />
          </div>
          <p className="mt-3 text-xs text-ink-soft">Interactive route with distance &amp; elevation, powered by On The Go Map.</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-8 grid gap-10 lg:grid-cols-2 items-start">
        <Reveal>
          <div>
            <Kicker>course flythrough</Kicker>
            <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl">Watch the 5 km loop</h2>
            <div className="mt-6 mx-auto max-w-[320px] relative rounded-3xl overflow-hidden shadow-warm-lg aspect-[1206/1632] border border-black/5 bg-ink">
              <video
                className="absolute inset-0 h-full w-full object-cover"
                controls
                playsInline
                preload="metadata"
                poster={`${process.env.PUBLIC_URL}/media/course-poster.jpg`}
                data-testid="course-video"
              >
                <source src={`${process.env.PUBLIC_URL}/media/course-preview.mp4`} type="video/mp4" />
              </video>
            </div>
            <p className="mt-3 text-xs text-ink-soft flex items-center gap-2">
              <Play size={13} /> Full course flythrough, tap play to preview the route.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div>
            <Kicker>interactive map</Kicker>
            <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl">Find the start line</h2>
            <div className="mt-6 relative rounded-3xl overflow-hidden shadow-warm-lg aspect-video border border-black/5">
              <iframe
                className="absolute inset-0 h-full w-full"
                src={EVENT.location.maps}
                title="Casablanca Beach map"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="course-map"
              />
            </div>
            <div className="mt-5 rounded-2xl bg-white border border-black/5 p-6 shadow-warm">
              <p className="flex items-start gap-2 font-medium"><MapPin size={18} className="text-terracotta mt-0.5" /> {EVENT.location.name}</p>
              <p className="mt-1 text-sm text-ink-soft pl-6">{EVENT.location.address}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <CTAButton href={EVENT.location.directions}><Navigation size={15} /> Get directions</CTAButton>
                <span className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-cream-200 text-sm font-medium text-ink-soft">
                  <Car size={15} /> Free parking at Church on Hunter
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal>
          <Kicker>where to park</Kicker>
          <h2 className="mt-2 font-display font-semibold text-3xl sm:text-4xl">Park at Church on Hunter</h2>
          <div className="mt-6 grid gap-6 lg:grid-cols-5 items-stretch">
            <div className="lg:col-span-3 relative rounded-3xl overflow-hidden shadow-warm-lg border border-black/5 h-[320px] sm:h-[400px]">
              <iframe
                className="absolute inset-0 h-full w-full"
                src={EVENT.parking.maps}
                title="Church on Hunter parking map"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="parking-map"
              />
            </div>
            <div className="lg:col-span-2 rounded-3xl bg-forest text-cream shadow-warm-lg p-8 sm:p-10 flex flex-col justify-center" data-testid="parking-card">
              <div className="grid place-items-center h-14 w-14 rounded-2xl bg-ochre/20 text-ochre"><Car size={26} /></div>
              <p className="mt-6 flex items-start gap-2 font-semibold text-lg"><MapPin size={18} className="text-ochre mt-1 shrink-0" /> {EVENT.parking.name}</p>
              <p className="mt-1 text-sm text-cream/70 pl-6">{EVENT.parking.address}</p>
              <p className="mt-5 text-cream/80 leading-relaxed text-sm">
                Race-day parking is generously hosted by Church on Hunter, a short walk from the start line at Casablanca Beach. Please arrive early, follow the volunteers' directions, and leave the beach lot for accessible parking and drop-offs.
              </p>
              <div className="mt-6">
                <CTAButton href={EVENT.parking.directions}><Navigation size={15} /> Directions to parking</CTAButton>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
